import { compareModelKey } from "../../decorators/StoreIdEquality";
import { StoreName } from "../../decorators/StoreNameDecorator";
import API from "../../models/API";
import Model, { ModelData, ModelStoreKey } from "../../models/Model";
import { ModelPages } from "../../models/ModelPages";
import { AbstractRemovableStore } from "../RemovableStore";
import { AbstractSaveableStore } from "../SaveableStore";

type ModelSaveResult = {
  /**
   * Was save successfull
   */
  successful: boolean;
  /**
   * Saved Model data. NULL if successful = false.
   */
  model: ModelData | null;
  // ignore
  isNew: boolean;
  /**
   * List of AcceleratorBatch UUIDs that should be reprocessed.
   */
  affectedBatches: string[];
  /** Created model pages */
  modelPages: ModelPages | undefined;
};
@StoreName("ModelStore")
class ModelStoreClass<__ extends ModelStoreKey, _ extends Model>
  extends AbstractSaveableStore<ModelStoreKey, Model>
  implements AbstractRemovableStore<ModelStoreKey, Model>
{
  removeAll(): Model[] {
    return this.values.splice(0, this.values.length);
  }
  remove(key: ModelStoreKey): Model | undefined {
    // get value by key
    const value = this.getWithoutLookup(key);

    if (!value) return;

    return this.values.splice(this.values.indexOf(value), 1)[0];
  }
  removeByValue(value: Model): void {
    const index: number = this.values.indexOf(value);
    if (index == -1) return;
    this.values.splice(this.values.indexOf(value), 1);
  }
  storedObjectKeysEquals(a: __, b: __): boolean {
    return compareModelKey(a, b);
  }

  /**
   * Saves a new version of a Model, by stripping out the Model.uuid before calling _saveByValue
   * @param value
   * @param batchInstanceUuid
   * @see _saveByValue
   */
  saveNew(
    value: Model,
    batchInstanceUuid?: string,
    documentId?: string,
  ): Promise<ModelSaveResult> {
    const modelData = value.toModelData();
    modelData.uuid = undefined;
    return this._saveByValue(modelData, batchInstanceUuid, documentId);
  }

  saveModified(
    value: Model,
    batchInstanceUuid?: string,
  ): Promise<ModelSaveResult> {
    const modelData = value.toModelData();
    return this._saveByValue(modelData, batchInstanceUuid);
  }

  /** @deprecated use saveNew or saveModified instead */
  async saveByValue(value: Model): Promise<boolean> {
    throw new Error("saveByValue is not supported in ModelStore");
  }

  async _saveByValue(
    value: ModelData,
    batchInstanceUuid?: string,
    documentId?: string,
  ): Promise<ModelSaveResult> {
    const name = value.name;
    const fail = {
      affectedBatches: [],
      isNew: false,
      model: null,
      successful: false,
      modelPages: undefined,
    };
    if (name == null || name.trim() === "") return fail;
    const response = API.post("/v0/model", null, {
      model: value,
      batchInstanceUuid,
      documentId,
    })
      .then((value: Response) => value.json())
      .then((value: object) => value as ModelSaveResult)
      .catch(() => fail);

    return response;
  }

  /**
   * Fetch a Model by Name and BatchClassId.
   * @param key
   * @returns
   */
  async lookup(key: __): Promise<Model | undefined> {
    return await API.get(
      `/v0/model/${key.batchClassIdentifier}/${encodeURIComponent(key.name)}`,
    )
      .then(async (value: Response) => {
        if (!value.ok) {
          return undefined;
        }
        const modelData: ModelData = (await value.json()) as ModelData;
        if (modelData) {
          return new Model(modelData);
        } else {
          return undefined;
        }
      })
      .catch(() => {
        return undefined;
      });
  }
  /**
   * Fetch a Model by UUID.
   * @param uuid
   * @returns
   */
  async lookupByUuid(uuid: string): Promise<Model | undefined> {
    return await API.get(`/v0/model/${uuid}`)
      .then(async (value: Response) => {
        if (!value.ok) {
          return undefined;
        }
        const modelData: ModelData = (await value.json()) as ModelData;
        if (modelData) {
          return new Model(modelData);
        } else {
          return undefined;
        }
      })
      .catch(() => {
        return undefined;
      });
  }

  /**
   * Get a Model from Store if the specific version is already loaded, or lookup from API.
   * @param id Model Key
   * @param version Model.version to compare against stored Model
   */
  async lookupUnlessStored(
    id: __,
    version: number,
  ): Promise<Model | undefined> {
    const stored = this.getWithoutLookup(id);
    if (stored && stored.version === version) {
      return stored;
    }
    return this.lookup(id);
  }

  /**
   * Get a Model from Store if the specific version is already loaded, or lookup from API.
   * @param uuid Model UUID
   * @param version Model.version to compare against stored Model
   */
  async lookupByUuidUnlessStored(
    uuid: string,
    version: number,
  ): Promise<Model | undefined> {
    const stored = this.values.filter((x) => x.uuid === uuid)[0];
    if (stored && stored.version === version) {
      return stored;
    }
    return this.lookupByUuid(uuid);
  }

  /**
   * Caller responsible for calling ModelStore.remove
   * @param key
   * @returns
   */
  async delete(key: __): Promise<boolean> {
    const model = this.getWithoutLookup(key);
    if (!model || !model.uuid) return false;
    return await this.deleteByValue(model);
  }

  async deleteByValue(model: Model): Promise<boolean> {
    return await API.delete(`/v0/model/${model.uuid}`, {
      batchClassId: model.storeId.batchClassIdentifier,
    })
      .then((value: Response) => value.json())
      .then((value: any): boolean => value)
      .catch(() => {
        return false;
      });
  }

  getManyByUuidWithoutLookup(uuids: string[]): Array<Model> {
    return this.values.filter((x) => uuids.includes(x.uuid!));
  }

  getAll(): Array<Model> {
    return this.values;
  }

  //@ts-expect-error
  _vamp(values) {
    //@ts-expect-error
    this.values = values.map((r) => new Model(r));
  }
  _unvamp() {
    return this.values;
  }
}

const ModelStore = new ModelStoreClass();
export { ModelStore, ModelStoreClass, ModelSaveResult };
